/**
 * Route-Messfühler (Ticket 2765): „Bluetooth bricht ab, Martin muss in den
 * Einstellungen umschalten." Ohne Gerät ist die Ursache nicht zu belegen —
 * darum schreibt jeder Routenwechsel, den die Seite sieht, EINE Zeile
 * `[route] …` ins Server-Protokoll (/api/debug/ptt-timing):
 * Grund, Uhrzeit, Eingänge vorher/nachher, aktiver Ausgang, Zustand des
 * warmen Mikros. Nur Messung, kein Eingriff in die Tonsitzung.
 */
import { meldeSpur } from "./mikro-spur.js";
import { ausgabeKuerzel } from "./playback-hifi.js";

export interface RouteMessung {
  grund: string;
  vorher: readonly string[];
  nachher: readonly string[];
  ausgang: string | null;
  warm: string;
  zeit?: Date;
}

const MAX_NAME = 24;

function kurz(namen: readonly string[]): string {
  if (!namen.length) return "-";
  return namen.map((n) => (n || "?").replace(/[\s|=]+/g, "_").slice(0, MAX_NAME)).join(",");
}

/** Die Messzeile — feste Reihenfolge, key=value, unter 400 Zeichen. */
export function routeZeile(m: RouteMessung): string {
  const zeit = (m.zeit ?? new Date()).toISOString().slice(11, 19);
  const neu = m.nachher.filter((n) => !m.vorher.includes(n));
  const weg = m.vorher.filter((n) => !m.nachher.includes(n));
  return [
    `grund=${m.grund}`,
    `um=${zeit}Z`,
    `ausgang=${m.ausgang ?? "?"}`,
    `warm=${m.warm}`,
    `neu=${kurz(neu)}`,
    `weg=${kurz(weg)}`,
    `jetzt=${kurz(m.nachher)}`,
  ]
    .join(" ")
    .slice(0, 380);
}

/** Zustand einer gehaltenen Mikro-Spur als ein Wort. */
export function spurZustand(track: { readyState?: string; muted?: boolean } | null | undefined): string {
  if (!track) return "keins";
  if (track.readyState === "ended") return "beendet";
  return track.muted ? "stumm" : "live";
}

export function meldeRoute(m: RouteMessung): void {
  meldeSpur("route", routeZeile(m));
}

async function eingaenge(): Promise<string[]> {
  try {
    const l = await navigator.mediaDevices.enumerateDevices();
    return l.filter((d) => d.kind === "audioinput").map((d) => d.label);
  } catch {
    return [];
  }
}

/** Der Ausgang der Tonsitzung jetzt (`ausgabeKuerzel`), `null` im Browser oder ohne Antwort der App. Auch für tts-queue.ts. */
export async function ausgangJetzt(): Promise<string | null> {
  try {
    const m = await import("./native-recorder.js");
    const st = await m.getAudioSessionState();
    return ausgabeKuerzel(st?.outputs) ?? null;
  } catch {
    return null;
  }
}

let letzteEingaenge: string[] = [];

/** Einmal beim Start: Listener auf `devicechange`. `warmSpur` liefert die gehaltene Spur. */
export function installiereRouteMessung(
  warmSpur: () => { readyState?: string; muted?: boolean } | null | undefined,
): void {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.addEventListener) return;
  void eingaenge().then((e) => (letzteEingaenge = e));
  navigator.mediaDevices.addEventListener("devicechange", () => {
    void (async () => {
      const vorher = letzteEingaenge;
      const nachher = await eingaenge();
      letzteEingaenge = nachher;
      meldeRoute({
        grund: "devicechange",
        vorher,
        nachher,
        ausgang: await ausgangJetzt(),
        warm: spurZustand(warmSpur()),
      });
    })();
  });
}

/** Spur-Ende, das nicht von uns kam (Gerät weg, Verbindung gerissen) — melden. */
export function beobachteSpurEnde(
  track: MediaStreamTrack,
  warmSpur: () => { readyState?: string; muted?: boolean } | null | undefined,
  eigenesStopp: () => boolean,
): void {
  track.addEventListener("ended", () => {
    if (eigenesStopp()) return;
    void ausgangJetzt().then((ausgang) =>
      meldeRoute({
        grund: "spur-ende-fremd",
        vorher: [track.label],
        nachher: [],
        ausgang,
        warm: spurZustand(warmSpur()),
      }),
    );
  });
}
